import React, { useState, useEffect } from 'react'
import axios from 'axios'
import DefaultAvatar from '@/pages/profile/components/DefaultAvatar.tsx'
import { ENV } from '../constants.ts'

interface ImageInputProps {
  imageUrl?: string
  placeholderUrl?: string
  firstName?: string | null
  lastName?: string | null
  onImageChange: (file: File | null) => void
}

const ImageInput: React.FC<ImageInputProps> = ({
  imageUrl,
  placeholderUrl,
  firstName,
  lastName,
  onImageChange,
}) => {
  const [image, setImage] = useState<string | null>(imageUrl || null)

  // Function to fetch the image from the API
  const fetchImage = async () => {
    if (imageUrl) {
      try {
        const response = await axios.get(
          `${ENV.VITE_API_URL}/admin/download-file?path=${imageUrl}`,
          {
            responseType: 'blob', // Ensure you get the image as a Blob
          }
        )
        const imageObjectURL = URL.createObjectURL(response.data)
        setImage(imageObjectURL)
      } catch (error) {
        // Set image to placeholder if error occurs
        setImage(null)
      }
    }
  }

  // Fetch image on component mount or when imageUrl changes
  useEffect(() => {
    fetchImage()
  }, [imageUrl])

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) {
      setImage(URL.createObjectURL(file))
      onImageChange(file) // Pass the file to the parent component
    }
  }
  const removeImage = () => {
    setImage(null)
    onImageChange(null)
  }

  return (
    <div className="flex justify-center items-center">
      <div className="relative w-20 h-20">
        <input
          type="file"
          accept=".png, .jpg, .jpeg"
          name="avatar"
          className="hidden"
          id="imageUpload"
          onChange={handleImageChange}
        />
        <input
          type="hidden"
          name="avatar_remove"
          value={image ? 'false' : 'true'}
        />

        {/* Remove button */}
        {image && (
          <button
            className="absolute z-10 top-0 right-0 w-5 h-5 bg-light shadow rounded-full flex items-center justify-center"
            onClick={removeImage}
          >
            <svg
              className="fill-dark"
              xmlns="http://www.w3.org/2000/svg"
              height="14"
              width="14"
              viewBox="0 0 14 14"
            >
              <path d="M7 0C3.13401 0 0 3.13401 0 7C0 10.866 3.13401 14 7 14C10.866 14 14 10.866 14 7C14 3.13401 10.866 0 7 0ZM9.82843 9.82843L7 7L4.17157 9.82843L3.29289 8.94975L6.12132 6.12132L3.29289 3.29289L4.17157 2.41421L7 5.24264L9.82843 2.41421L10.7071 3.29289L7.87868 6.12132L10.7071 8.94975L9.82843 9.82843Z" />
            </svg>
          </button>
        )}

        {/* Image Preview */}
        <label
          htmlFor="imageUpload"
          className="absolute inset-0 cursor-pointer"
        >
          {image || placeholderUrl ? (
            <div
              className={`w-20 h-20 rounded-full border-2 ${
                image ? 'border-success' : 'border-gray-300'
              } relative flex justify-center items-center`}
              style={{
                backgroundImage: `url(${image || placeholderUrl})`,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
              }}
            />
          ) : (
            <DefaultAvatar firstName={firstName} lastName={lastName} />
          )}
        </label>

        {/* Tooltip */}
        {image && (
          <div className="absolute -top-5 right-0 z-20 text-xs p-1 bg-gray-700 text-white rounded hidden group-hover:block">
            Click to remove or revert
          </div>
        )}
      </div>
    </div>
  )
}

export default ImageInput
